import { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Calendar,
  CalendarClock,
  CalendarDays,
  Clock,
  ExternalLink,
  MapPin,
  Monitor,
  Trash2,
  Trophy,
  Users,
  XCircle,
} from 'lucide-react';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { playEventService } from '@/services/playEvent';
import { formatTimeHHMM } from '@/utils/timeFormat';
import type { ScheduleOccurrence } from '@/utils/scheduleOccurrences';
import type { PlayEvent, PlayEventParticipant, PlayEventStatus } from '@court-sync/shared';
import { getPlayEventParticipantDisplayName } from '@court-sync/shared';
import {
  PLAY_EVENT_FORMAT_LABELS,
  PLAY_PARTICIPANT_STATUS_LABELS,
  PLAY_EVENT_STATUS_LABELS,
} from '@/lib/playEventLabels';
import { PlayEventRescheduleModal } from '@/components/play/PlayEventRescheduleModal';
import { CancelPlayEventConfirmDialog } from '@/components/play/CancelPlayEventConfirmDialog';
import { DeletePlayEventConfirmDialog } from '@/components/play/DeletePlayEventConfirmDialog';

export interface PlayEventScheduleDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  occurrence: ScheduleOccurrence | null;
  organizationId: string | undefined;
  onAfterAction?: () => void | Promise<void>;
}

const descriptionChipClass = 'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded';

function playEventStatusChipClass(status: PlayEventStatus): string {
  switch (status) {
    case 'published':
    case 'in_progress':
      return `${descriptionChipClass} bg-green-100 text-green-900 dark:bg-green-900/40 dark:text-green-100`;
    case 'check_in_open':
      return `${descriptionChipClass} bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100`;
    case 'cancelled':
      return `${descriptionChipClass} bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100`;
    case 'completed':
      return `${descriptionChipClass} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`;
    case 'draft':
    default:
      return `${descriptionChipClass} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`;
  }
}

export function PlayEventScheduleDetailDrawer({
  isOpen,
  onClose,
  occurrence,
  organizationId,
  onAfterAction,
}: PlayEventScheduleDetailDrawerProps) {
  const [participants, setParticipants] = useState<PlayEventParticipant[]>([]);
  const [formatLabel, setFormatLabel] = useState<string | null>(null);
  const [statusLabel, setStatusLabel] = useState<string | null>(null);
  const [eventStatus, setEventStatus] = useState<PlayEvent['status'] | null>(null);
  const [isSeriesSession, setIsSeriesSession] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const eventId = occurrence?.lessonId;

  const loadEvent = useCallback(async () => {
    if (!organizationId || !eventId) return;
    setLoading(true);
    try {
      const res = await playEventService.getEvent(organizationId, eventId);
      if (res.success && res.data) {
        const { event, participants: loadedParticipants } = res.data;
        setParticipants(loadedParticipants);
        setFormatLabel(PLAY_EVENT_FORMAT_LABELS[event.format] ?? event.format);
        setStatusLabel(PLAY_EVENT_STATUS_LABELS[event.status] ?? event.status);
        setEventStatus(event.status);
        setIsSeriesSession(Boolean(event.seriesId));
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId, eventId]);

  useEffect(() => {
    if (!isOpen || !occurrence) {
      setParticipants([]);
      setFormatLabel(null);
      setStatusLabel(null);
      setEventStatus(null);
      setIsSeriesSession(false);
      setCancelDialogOpen(false);
      setDeleteDialogOpen(false);
      setRescheduleOpen(false);
      return;
    }
    void loadEvent();
  }, [isOpen, occurrence, loadEvent]);

  const handleCancelEvent = async () => {
    if (!organizationId || !eventId) return;
    setActionLoading(true);
    try {
      const res = await playEventService.cancelEvent(organizationId, eventId);
      if (res.success) {
        toast.success('Session cancelled');
        setCancelDialogOpen(false);
        await loadEvent();
        await onAfterAction?.();
      } else {
        toast.error(res.error ?? 'Failed to cancel session');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!organizationId || !eventId) return;
    setActionLoading(true);
    try {
      const res = await playEventService.deleteEvent(organizationId, eventId);
      if (res.success) {
        toast.success('Session deleted');
        setDeleteDialogOpen(false);
        onClose();
        await onAfterAction?.();
      } else {
        toast.error(res.error ?? 'Failed to delete session');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleRescheduleConfirm = async (startsAt: Date, endsAt: Date) => {
    if (!organizationId || !eventId) return;
    setActionLoading(true);
    try {
      const res = await playEventService.updateEvent(organizationId, eventId, {
        startsAt,
        endsAt,
      });
      if (res.success) {
        toast.success('Event rescheduled');
        setRescheduleOpen(false);
        onClose();
        await onAfterAction?.();
      } else {
        throw new Error(res.error ?? 'Failed to reschedule event');
      }
    } finally {
      setActionLoading(false);
    }
  };

  if (!occurrence) return null;

  const registeredParticipants = participants.filter(
    (participant) => participant.status === 'registered' || participant.status === 'checked_in'
  );

  const capacity = occurrence.maxCapacity;
  const participantCount = loading ? null : registeredParticipants.length;
  const enrollmentLabel =
    participantCount != null
      ? `${participantCount}${capacity > 0 ? ` / ${capacity}` : ''}`
      : capacity > 0
        ? `— / ${capacity}`
        : '—';

  const canReschedule =
    eventStatus === 'published' || eventStatus === 'check_in_open';
  const canCancel =
    eventStatus != null && eventStatus !== 'cancelled' && eventStatus !== 'completed';
  const canDelete = eventStatus != null && eventStatus !== 'in_progress';
  const hasSecondaryActions = canReschedule || canCancel || canDelete;
  const canOpenDisplays = Boolean(eventId);

  const dateLabel = format(occurrence.date, 'EEEE, MMM d, yyyy');
  const drawerDescription = (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span
        className={`${descriptionChipClass} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`}
      >
        <Calendar className="h-3 w-3 shrink-0" aria-hidden />
        {dateLabel}
      </span>
      <span
        className={`${descriptionChipClass} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`}
      >
        <Clock className="h-3 w-3 shrink-0" aria-hidden />
        {formatTimeHHMM(occurrence.startTime)} – {formatTimeHHMM(occurrence.endTime)}
      </span>
      {occurrence.courtLabel ? (
        <span
          className={`${descriptionChipClass} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`}
        >
          <MapPin className="h-3 w-3 shrink-0" aria-hidden />
          {occurrence.courtLabel}
        </span>
      ) : null}
      {formatLabel ? (
        <span
          className={`${descriptionChipClass} bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100`}
        >
          <Trophy className="h-3 w-3 shrink-0" aria-hidden />
          {formatLabel}
        </span>
      ) : null}
      {isSeriesSession ? (
        <span
          className={`${descriptionChipClass} bg-indigo-100 text-indigo-900 dark:bg-indigo-900/40 dark:text-indigo-100`}
        >
          <CalendarDays className="h-3 w-3 shrink-0" aria-hidden />
          Series session
        </span>
      ) : null}
      {statusLabel && eventStatus ? (
        <span className={playEventStatusChipClass(eventStatus)}>{statusLabel}</span>
      ) : loading ? (
        <span
          className={`${descriptionChipClass} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`}
        >
          Loading…
        </span>
      ) : null}
    </span>
  );

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title={occurrence.lessonName}
        description={drawerDescription}
        width="3xl"
        footer={
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild size="sm" className="min-w-0 flex-1 gap-1.5 sm:flex-initial">
              <Link to={`/admin/play-events/${eventId}`} onClick={onClose}>
                Open event
              </Link>
            </Button>
            {canOpenDisplays ? (
              <>
                <Button
                  asChild
                  type="button"
                  variant="outline"
                  size="sm"
                  className="hidden gap-1.5 sm:inline-flex"
                >
                  <Link
                    to={`/admin/play-events/${eventId}/kiosk`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Monitor className="h-4 w-4 shrink-0" aria-hidden />
                    Kiosk
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  </Link>
                </Button>
                <Button
                  asChild
                  type="button"
                  variant="outline"
                  size="sm"
                  className="hidden gap-1.5 sm:inline-flex"
                >
                  <Link
                    to={`/admin/play-events/${eventId}/board`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Monitor className="h-4 w-4 shrink-0" aria-hidden />
                    Public board
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  </Link>
                </Button>
              </>
            ) : null}
            {hasSecondaryActions || canOpenDisplays ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={actionLoading}
                    className="shrink-0"
                  >
                    More…
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={4} className="w-56">
                  {canOpenDisplays ? (
                    <>
                      <DropdownMenuItem asChild className="sm:hidden">
                        <Link
                          to={`/admin/play-events/${eventId}/kiosk`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cursor-pointer gap-2"
                        >
                          <Monitor className="h-4 w-4 shrink-0" />
                          Kiosk
                          <ExternalLink className="ml-auto h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="sm:hidden">
                        <Link
                          to={`/admin/play-events/${eventId}/board`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cursor-pointer gap-2"
                        >
                          <Monitor className="h-4 w-4 shrink-0" />
                          Public board
                          <ExternalLink className="ml-auto h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                        </Link>
                      </DropdownMenuItem>
                    </>
                  ) : null}
                  {canOpenDisplays && hasSecondaryActions ? (
                    <DropdownMenuSeparator className="sm:hidden" />
                  ) : null}
                  {canReschedule ? (
                    <DropdownMenuItem
                      onSelect={() => setRescheduleOpen(true)}
                      className="cursor-pointer gap-2"
                    >
                      <CalendarClock className="h-4 w-4 shrink-0" />
                      Reschedule session
                    </DropdownMenuItem>
                  ) : null}
                  {canCancel ? (
                    <DropdownMenuItem
                      onSelect={() => setCancelDialogOpen(true)}
                      className="cursor-pointer gap-2 text-red-700 focus:text-red-700 dark:text-red-300 dark:focus:text-red-300"
                    >
                      <XCircle className="h-4 w-4 shrink-0" />
                      Cancel session
                    </DropdownMenuItem>
                  ) : null}
                  {canDelete ? (
                    <DropdownMenuItem
                      onSelect={() => setDeleteDialogOpen(true)}
                      className="cursor-pointer gap-2 text-red-700 focus:text-red-700 dark:text-red-300 dark:focus:text-red-300"
                    >
                      <Trash2 className="h-4 w-4 shrink-0" />
                      Delete session
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        }
      >
        <div className="flex flex-col min-h-full gap-4 text-sm text-gray-800 dark:text-gray-200">
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h4 className="font-medium text-gray-900 dark:text-white flex min-w-0 items-center gap-2">
                <Users className="h-4 w-4 shrink-0" />
                <span className="truncate">
                  Participants
                  {!loading && registeredParticipants.length > 0 ? (
                    <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                      {' '}
                      ({registeredParticipants.length})
                    </span>
                  ) : null}
                </span>
                {!loading ? (
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                    · {enrollmentLabel}
                  </span>
                ) : null}
              </h4>
            </div>
            {loading ? (
              <p className="text-gray-500 text-xs">Loading…</p>
            ) : registeredParticipants.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-xs">No registered players yet.</p>
            ) : (
              <>
                <div
                  className="hidden sm:grid pb-1.5 mb-2.5 border-b border-gray-200 dark:border-gray-700 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 grid-cols-[minmax(0,1fr)_6.5rem] items-end gap-x-3"
                  aria-hidden
                >
                  <span className="min-w-0">Player</span>
                  <span className="text-center">Status</span>
                </div>
                <ul className="space-y-0 sm:space-y-2 flex-1 min-h-0 overflow-y-auto pr-1 [scrollbar-width:thin]">
                  {registeredParticipants.map((participant) => (
                    <li
                      key={participant.id}
                      className="border-b border-gray-100 dark:border-gray-700 last:border-0 py-3 space-y-2.5 sm:py-0 sm:space-y-0 sm:pb-2 sm:grid sm:grid-cols-[minmax(0,1fr)_6.5rem] sm:items-center sm:gap-x-3 sm:gap-y-1"
                    >
                      <div className="min-w-0">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {getPlayEventParticipantDisplayName(participant)}
                        </span>
                        {participant.participantType !== 'member' ? (
                          <span className="ml-2 text-[10px] px-1.5 py-0 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 capitalize">
                            {participant.participantType}
                          </span>
                        ) : null}
                      </div>
                      <div className="flex min-w-0 items-center gap-2 sm:justify-center">
                        <span className="w-14 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:hidden">
                          Status
                        </span>
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                          {PLAY_PARTICIPANT_STATUS_LABELS[participant.status]}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </Drawer>

      <CancelPlayEventConfirmDialog
        open={cancelDialogOpen}
        onOpenChange={(open) => !actionLoading && setCancelDialogOpen(open)}
        loading={actionLoading}
        onConfirm={handleCancelEvent}
        context="session"
        eventName={occurrence.lessonName}
        isSeriesSession={isSeriesSession}
        isInProgress={eventStatus === 'in_progress'}
      />

      <DeletePlayEventConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => !actionLoading && setDeleteDialogOpen(open)}
        loading={actionLoading}
        onConfirm={handleDeleteEvent}
        context="session"
        eventName={occurrence.lessonName}
      />

      <PlayEventRescheduleModal
        isOpen={rescheduleOpen}
        occurrence={occurrence}
        eventName={occurrence.lessonName}
        isLoading={actionLoading}
        onCancel={() => setRescheduleOpen(false)}
        onConfirm={handleRescheduleConfirm}
      />
    </>
  );
}
